import React from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { MapPin, Clock, Phone, Navigation, Calendar } from "lucide-react";
import { clinicData } from "../mock";
import { useNavigate } from "react-router-dom"; // ✅ import navigate

const BranchesSection = () => {
  const navigate = useNavigate(); // ✅ hook

  return (
    <section id="branches" className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/50 to-transparent"></div>
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-[#204080]/5 rounded-full blur-3xl -translate-x-1/3"></div>
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-[#2E5AAC]/5 rounded-full blur-3xl translate-x-1/3"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-[#204080]/10 text-[#204080] px-5 py-3 rounded-full text-base font-semibold mb-6">
            <MapPin size={20} />
            <span>Our Locations</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#3a5bb8] drop-shadow-sm mb-4 tracking-tight leading-snug font-serif">
            Convenient Locations Accessible Care
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-serif">
            Visit us at either of our state-of-the-art facilities, strategically
            located to serve our community with excellence and convenience.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {clinicData.branches.map((branch, index) => (
            <Card
              key={branch.id}
              className="group overflow-hidden bg-white shadow-lg hover:shadow-2xl border-0 transition-all duration-500 transform hover:-translate-y-2"
            >
              {/* Header */}
              <div className="relative p-8 bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-x-8 -translate-y-8"></div>
                <div className="relative space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">{branch.name}</h3>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin size={18} className="text-blue-200 mt-1 flex-shrink-0" />
                    <p className="text-blue-100">{branch.address}</p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Phone size={16} className="text-blue-200" />
                    <span className="text-blue-100">{branch.phone}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                {/* Operating Hours */}
                <div className="space-y-4">
                  <h4 className="flex items-center space-x-2 text-lg font-semibold text-[#204080]">
                    <Clock size={18} />
                    <span>Operating Hours</span>
                  </h4>

                  <div className="space-y-2 text-gray-600">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="font-medium">Monday - Saturday</span>
                      <span>{branch.hours.weekdays}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="font-medium">Sunday</span>
                      <span>{branch.hours.sunday}</span>
                    </div>
                  </div>
                </div>

                {/* Specialties */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-[#204080]">
                    Services
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {branch.services.map((specialty, specialtyIndex) => (
                      <Badge
                        key={specialtyIndex}
                        variant="outline"
                        className="text-[#204080] border-[#204080]/30 hover:bg-[#204080]/10 transition-colors duration-200"
                      >
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-3 pt-4 border-t border-gray-100">
                  <button
                    className="flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    onClick={() =>
                     navigate("/book-appointment", { state: { branch: branch.name } })
                    }
                  >
                    <Calendar size={16} />
                    <span>Book at {branch.name}</span>
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      className="flex items-center justify-center space-x-2 border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white px-4 py-2 rounded-lg font-medium transition-all duration-300"
                      onClick={() =>
                        window.open(
                          index === 0
                            ? "https://maps.app.goo.gl/eDPQqHNu2GkENEUZ6"
                            : "https://maps.app.goo.gl/ohyaieGLcKcDtc9x9",
                          "_blank"
                        )
                      }
                    >
                      <Navigation size={14} />
                      <span>Directions</span>
                    </button>
                    <a
                      href={`tel:${branch.phone.replace(/\D/g, "")}`}
                      className="flex items-center justify-center space-x-2 border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white px-4 py-2 rounded-lg font-medium transition-all duration-300"
                    >
                      <Phone size={14} />
                      <span>Call Now</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </Card>
          ))}
        </div>

        {/* Contact Banner */}
        <Card className="p-8 bg-gradient-to-r from-slate-50 to-blue-50 border border-gray-200 shadow-lg">
          <div className="text-center space-y-4">
            <h3 className="text-2xl font-bold text-[#204080]">
              Can't Visit Our Locations?
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We offer mobile physiotherapy services and virtual consultations
              for your convenience. Contact us to learn about our flexible care
              options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate("/home-visit")}
              className="px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                Schedule Home Visit
              </button>
              <button 
              onClick={() => navigate("/virtual-consultation")}
              className="px-8 py-3 border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white font-semibold rounded-lg transition-all duration-300">
                Virtual Consultation
              </button>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default BranchesSection;
